/* eslint-disable class-methods-use-this */
import ApiController from '../api/apiController';
import { authEvents } from './constants/authEvents';
import { setCookie, getCookie, deleteCookie } from '../utils/cookie';
import { ee } from '../eventEmitter';

const isLoggedResponseParam = 'user-is-logged';
const userSessionCookieName = 'session-token';

export default class AppAuthController {
    static instance;
    #isGuest = null; // Пользователь не авторизован
    #userData = null;
    #sessionToken = null;
    #apiController = null;
    #userLoginChecked = false;
    #passwordChangeParams = null;
    #isDemoMode = false;
    #gameDisabled = false;

    constructor(options) {
        if (AppAuthController.instance) {
            // eslint-disable-next-line no-constructor-return
            return AppAuthController.instance;
        }

        this.#isDemoMode = options.isDemoMode || false;
        this.#gameDisabled = options.gameDisabled || false;

        this.#apiController = new ApiController();
        this.initLogoutButtons();
        this.checkSessionToken();

        AppAuthController.instance = this;
    }

    get isGuest() {
        return this.#isGuest;
    }

    set isGuest(flag) {
        this.#isGuest = flag;
    }

    get userLoginChecked() {
        return this.#isGuest !== null;
    }

    get userData() {
        return this.#userData;
    }

    set passwordChangeParams(params) {
        this.#passwordChangeParams = params;
    }

    checkSessionToken() {
        const token = getCookie(userSessionCookieName) || null;

        if (token) {
            this.setSessionToken(token);
        }
    }

    clearSessionToken() {
        this.#sessionToken = null;
        this.#apiController.sessionToken = null;
        deleteCookie(userSessionCookieName);
    }

    setSessionToken(token) {
        const expDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

        setCookie(userSessionCookieName, token, {
            expires: expDate
        });

        this.#sessionToken = token;
        this.#apiController.sessionToken = token;
    }

    initLogoutButtons() {
        const buttons = Array.from(document.querySelectorAll('.js-logout-button'));

        buttons.forEach((button) => {
            button.addEventListener('click', this.logout.bind(this));
        });
    }

    updateUserData(data) {
        if (!data) return;

        const token = data['session-token'];

        if (token) {
            this.setSessionToken(token);
        }

        this.#userData = { ...data };

        ee.emit(authEvents.UPDATE_USER_DATA, this.#userData);
    }

    async getIsUserLogged() {
        if (this.#isDemoMode || this.#gameDisabled) {
            this.#isGuest = true;
            this.clearSessionToken();
            this.#userLoginChecked = true;

            return false;
        }

        const response = await this.#apiController.getIsUserLogged();

        if (response?.data?.[isLoggedResponseParam]) {
            this.#isGuest = false;
        } else {
            this.#isGuest = true;
            this.clearSessionToken();
        }

        this.updateUserData(response.data?.user);

        this.#userLoginChecked = true;

        return response;
    }

    async login(data) {
        const loginResponse = await this.#apiController.login(data);

        if (loginResponse.error) return loginResponse;

        this.#isGuest = false;
        this.updateUserData(loginResponse.data?.user || loginResponse.data?.[0]?.user);
        ee.emit(authEvents.LOGIN);

        return loginResponse;
    }

    async logout(event) {
        if (this.#isGuest) return false;

        const button = event.target.closest('.js-logout-button');

        button?.classList.add('loading');

        const response = await this.#apiController.logout();

        if (!response.error) {
            this.#isGuest = true;
            this.clearSessionToken();
            ee.emit(authEvents.LOGOUT);
        }

        button?.classList.remove('loading');

        return response;
    }

    async register(data) {
        const regResponse = await this.#apiController.register(data);

        if (regResponse.error) return regResponse;

        this.#isGuest = false;

        // console.log(regResponse.data?.user || regResponse.data?.[0]?.user);
        this.updateUserData(regResponse.data?.user || regResponse.data?.[0]?.user);

        ee.emit(authEvents.REGISTER);

        return regResponse;
    }

    async getUserData(id) {
        const response = await this.#apiController.getUserData({ id });

        this.updateUserData(response.data?.user);

        return response;
    }

    async resetPassword(data) {
        const response = await this.#apiController.resetPassword(data);

        if (!response.error) {
            ee.emit(authEvents.RESET_PASSWORD);
        }

        return response;
    }

    async setNewPassword(data) {
        const dataToSend = {
            ...data,
            ...this.#passwordChangeParams
        };

        const response = await this.#apiController.setNewPassword(dataToSend);

        window.history.replaceState(null, '', window.location.pathname);

        if (!response.error) {
            ee.emit(authEvents.SET_NEW_PASSWORD);
        }

        return response;
    }
}
